"use client";

import { RefObject, useEffect } from "react";

import {
  ReadyEvent,
  patchDomReadyEvents,
} from "../helpers/patchDomReadyEvents.helper";

/**
 * Replays <script> tags neutralized by neutralizeScripts (marked with
 * data-cms-script) inside a container element.
 *
 * Scripts inserted via innerHTML strings are inert — they don't execute.
 * Each neutralized script is replaced with a live element, running them
 * sequentially so dependency order is preserved (mirrors the approach
 * used by CmsStaticPage for the meus-cupons wallet widget).
 *
 * Runs on every mount and every time `content` changes, since scripts are
 * always neutralized at the source: there's no "already executed by the
 * browser" case to detect, so replaying unconditionally is safe both on
 * the initial hydration pass and on subsequent client-side navigations.
 */

/**
 * How long to wait for a <script src> before moving on. Without it a single
 * request that never settles (ad blocker swallowing it, CDN stall) blocks
 * *every* remaining CMS script, since the chain is strictly serial.
 *
 * Trade-off: a script that times out and loads later still executes, but out
 * of order. Advancing out of order beats a permanently stuck chain.
 */
export const SCRIPT_LOAD_TIMEOUT_MS = 5_000;

// data-cms-type is consumed below to restore the original type; the sentinel
// and the inert type="text/plain" must never reach the live element.
const INTERNAL_ATTRS = new Set(["data-cms-script", "data-cms-type", "type"]);

export type CmsDomReadyListenerFailure = {
  cmsKey?: string;
  eventName: ReadyEvent;
  error: Error;
};

export function useReplayCmsScripts(
  containerRef: RefObject<HTMLElement | null>,
  content: string,
  cmsKey?: string,
  onListenerError?: (failure: CmsDomReadyListenerFailure) => void,
): void {
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const scripts = Array.from(
      el.querySelectorAll<HTMLScriptElement>("script[data-cms-script]"),
    );
    if (scripts.length === 0) return;

    let active = true;
    const pendingTimers = new Set<ReturnType<typeof setTimeout>>();

    function clearTimer(timer: ReturnType<typeof setTimeout>): void {
      clearTimeout(timer);
      pendingTimers.delete(timer);
    }

    // Workaround for legacy CMS scripts gated on DOMContentLoaded/load — see
    // patchDomReadyEvents.helper.ts for what this does and how to remove it.
    //
    // Scoped per-script rather than once for the whole chain: opened right
    // before a script that might register one of these listeners runs,
    // closed as soon as that script is known to be done. That keeps the
    // global patch window as small as the replay allows instead of it
    // spanning a chain that can be network-bound end to end. Each call gets
    // its own independent release — patchDomReadyEvents is ref-counted
    // specifically so that concurrent/overlapping windows (e.g. this
    // script's deferred close overlapping with the next one's open) don't
    // interfere with each other.
    //
    // pendingRelease tracks only the most recently opened window, purely as
    // an unmount safety net (below) — every window still releases itself on
    // its own via the paths below regardless of whether unmount calls it too
    // (patchDomReadyEvents's release is idempotent).
    let pendingRelease: (() => void) | null = null;
    function openWindow(): () => void {
      const release = patchDomReadyEvents((eventName, error) => {
        // No way to tell programmatically whether this is a regression in
        // the replay shim itself or a pre-existing incompatibility in the
        // CMS script — see the Datadog monitor's description for how to
        // triage that.
        onListenerError?.({ cmsKey, eventName, error });
      });
      pendingRelease = release;
      return release;
    }

    function next(i: number): void {
      if (!active || i >= scripts.length) return;
      const old = scripts[i];
      const live = document.createElement("script");
      for (const attr of Array.from(old.attributes)) {
        if (INTERNAL_ATTRS.has(attr.name)) continue;
        live.setAttribute(attr.name, attr.value);
      }
      const originalType = old.getAttribute("data-cms-type");
      if (originalType) live.type = originalType;

      if (old.src) {
        const release = openWindow();
        // Idempotent so a load event arriving after the timeout (or an
        // error after a load) can't advance the chain twice.
        let advanced = false;
        function advance(): void {
          if (advanced) return;
          advanced = true;
          clearTimer(timer);
          release();
          next(i + 1);
        }
        const timer = setTimeout(advance, SCRIPT_LOAD_TIMEOUT_MS);
        pendingTimers.add(timer);

        live.src = old.src;
        live.onload = advance;
        live.onerror = advance;
        old.replaceWith(live);
        return;
      }

      live.textContent = old.textContent;

      if (originalType === "module") {
        // Per spec, a script only fires load/error when it's "from an
        // external file" — an inline module never does, even though it
        // still always executes asynchronously (queued as a task, even with
        // no imports to fetch). There's no event to gate on, so advance on
        // the next task instead of synchronously: same fire-and-forget
        // trade-off as the timeout below, just with nothing to time out.
        const release = openWindow();
        old.replaceWith(live);
        const timer = setTimeout(() => {
          clearTimer(timer);
          release();
          next(i + 1);
        }, 0);
        pendingTimers.add(timer);
        return;
      }

      // Classic inline scripts execute synchronously as part of this same
      // insertion call, so the window only needs to span the call itself —
      // plus one microtask: if the script registers a DOMContentLoaded/load
      // listener, patchDomReadyEvents queues that listener's synthetic
      // dispatch as a microtask (installPatch), which only runs *after* this
      // synchronous call returns. Releasing in the same tick would tear down
      // the error-reporting callback before that dispatch — and any error it
      // throws — ever got a chance to run.
      const release = openWindow();
      try {
        old.replaceWith(live);
      } finally {
        queueMicrotask(release);
      }
      next(i + 1);
    }

    next(0);

    return () => {
      active = false;
      pendingTimers.forEach((timer) => clearTimeout(timer));
      pendingTimers.clear();
      pendingRelease?.();
    };
  }, [content, containerRef, cmsKey, onListenerError]);
}
